import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react"

export interface TipZeile {
  label: string
  wert: string
  farbe?: string
}

interface TipZustand {
  x: number
  y: number
  titel: string
  zeilen: TipZeile[]
}

interface TipApi {
  zeige: (x: number, y: number, titel: string, zeilen: TipZeile[]) => void
  verstecke: () => void
}

const TipContext = createContext<TipApi>({ zeige: () => {}, verstecke: () => {} })

export function useTip() {
  return useContext(TipContext)
}

/** Ein Tooltip für die ganze Seite: Wert zuerst, Bezeichnung danach, Linien-Schlüssel statt Kästchen. */
export function TipProvider({ children }: { children: ReactNode }) {
  const [tip, setTip] = useState<TipZustand | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ left: 0, top: 0 })

  const zeige = useCallback((x: number, y: number, titel: string, zeilen: TipZeile[]) => {
    setTip({ x, y, titel, zeilen })
  }, [])
  const verstecke = useCallback(() => setTip(null), [])

  useLayoutEffect(() => {
    if (!tip || !ref.current) return
    const r = ref.current.getBoundingClientRect()
    let left = tip.x + 14
    let top = tip.y + 14
    if (left + r.width > window.innerWidth - 8) left = tip.x - r.width - 14
    if (top + r.height > window.innerHeight - 8) top = tip.y - r.height - 14
    setPos({ left: Math.max(8, left), top: Math.max(8, top) })
  }, [tip])

  useEffect(() => {
    window.addEventListener("scroll", verstecke, { passive: true })
    return () => window.removeEventListener("scroll", verstecke)
  }, [verstecke])

  return (
    <TipContext.Provider value={{ zeige, verstecke }}>
      {children}
      {tip && (
        <div
          ref={ref}
          role="tooltip"
          className="pointer-events-none fixed z-50 grid min-w-[150px] max-w-[280px] gap-1 rounded-lg border border-hair/10 bg-surface px-3 py-2 text-xs shadow-lg"
          style={{ left: pos.left, top: pos.top }}
        >
          <div className="text-[12.5px] font-semibold">{tip.titel}</div>
          {tip.zeilen.map((z, i) => (
            <div key={i} className="flex items-baseline justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 text-ink2">
                {z.farbe && <i className="inline-block h-0.5 w-4 rounded" style={{ background: z.farbe }} />}
                {z.label}
              </span>
              <b className="tabular font-semibold">{z.wert}</b>
            </div>
          ))}
        </div>
      )}
    </TipContext.Provider>
  )
}

/** Breite eines Containers (für SVG-Diagramme, die sich anpassen). */
export function useBreite<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null)
  const [breite, setBreite] = useState(0)
  useEffect(() => {
    if (!ref.current) return
    const ro = new ResizeObserver((e) => setBreite(Math.round(e[0].contentRect.width)))
    ro.observe(ref.current)
    return () => ro.disconnect()
  }, [])
  return [ref, breite]
}
